// src/context/DataAndFilterContext.js
import React, { createContext, useState, useEffect, useRef } from 'react';
import { useGlobalLoading } from './GlobalLoadingContext'; // Import the global loading context
import BACKEND_URL from '../config';
import { useAuth } from './AuthContext';

export const DataAndFilterContext = createContext();

export const FilterProvider = ({ children }) => {
  const [data, setData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    conversation_sources: [],
    personas: [],
    jobsToBeDone: [],
    desiredOutcomes: [],
    solutionChoices: [],
    customTags: [],
    interviewDatasets: [],
  });
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    selectedSource: [],
    selectedPersona: [],
    selectedJobToBeDone: [],
    selectedDesiredOutcome: [],
    selectedSolutionChoice: [],
    selectedCustomTags: [],
    dateRange: 'All time',
    customDateRange: { from: null, to: null },
    selectedDatasets: [],
  });

  const { startLoading, stopLoading } = useGlobalLoading();
  const { token } = useAuth();

  // Track if we've already fetched conversations for this token
  const lastTokenRef = useRef(null);
  const hasFetchedRef = useRef(false);

  const fetchUniqueTags = async () => {
    if (!token) {
      console.warn('No access token yet; skipping fetchUniqueTags.');
      return [];
    }

    try {
      startLoading();
      const response = await fetch(`${BACKEND_URL}/api/conversations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (!response.ok) {
        console.error('Failed to fetch unique tags:', response.status);
        return [];
      }
  
      const result = await response.json();
  
      const uniqueCustomTags = new Set();
      result.filtered_data.forEach((conv) => {
        conv.segmentation_data?.custom_tags?.forEach((tag) => {
          uniqueCustomTags.add(tag);
        });
      });
  
      setFilterOptions((prevOptions) => ({
        ...prevOptions,
        customTags: Array.from(uniqueCustomTags),
      }));
  
      setData(result.filtered_data);
      setFilteredData(result.filtered_data);
  
      return Array.from(uniqueCustomTags);
    } catch (error) {
      console.error('Error fetching unique tags:', error);
      return [];
    } finally {
      stopLoading();
    }
  };

  const updateDataFile = (filename, updatedFileData) => {
    setData((prevData) =>
      prevData.map((file) => (file.filename === filename ? updatedFileData : file))
    );
    setFilteredData((prevFilteredData) =>
      prevFilteredData.map((file) => (file.filename === filename ? updatedFileData : file))
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      // No token yet: don’t try and don’t show as an error
      if (!token) {
        console.warn('No access token yet; skipping /api/conversations fetch.');
        return;
      }

      // 👇 Only fetch once per token
      if (hasFetchedRef.current && lastTokenRef.current === token) {
        console.log('[FilterProvider] Already fetched conversations for this token; skipping.');
        return;
      }
      hasFetchedRef.current = true;
      lastTokenRef.current = token;

      try {
        startLoading();

        const response = await fetch(`${BACKEND_URL}/api/conversations`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const result = await response.json();
          setData(result.filtered_data);

          const uniqueJobs = new Set();
          const uniqueOutcomes = new Set();
          const uniqueSolutions = new Set();
          const uniqueCustomTags = new Set();

          result.filtered_data.forEach((conv) => {
            conv.jobs_to_be_done?.forEach((job) => {
              uniqueJobs.add(job.job_to_be_done_category);
              uniqueOutcomes.add(job.desired_outcome_category);
            });
            if (conv.solution_choice_category) {
              uniqueSolutions.add(conv.solution_choice_category);
            }
            conv.segmentation_data?.custom_tags?.forEach((tag) => {
              uniqueCustomTags.add(tag);
            });
          });

          const datasets =
            (result.filter_options && result.filter_options.interview_datasets) || [];

          setFilterOptions({
            conversation_sources: result.filter_options.conversation_sources,
            personas: result.filter_options.personas,
            jobsToBeDone: Array.from(uniqueJobs),
            desiredOutcomes: Array.from(uniqueOutcomes),
            solutionChoices: Array.from(uniqueSolutions),
            customTags: Array.from(uniqueCustomTags),
            interviewDatasets: datasets,
          });

          setFilters((prev) => ({
            ...prev,
            selectedDatasets: datasets.map((d) => d.key),
          }));

          setFilteredData(result.filtered_data);
        } else {
          console.error('Failed to fetch data:', response.status);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        stopLoading();
      }
    };

    fetchData();
  }, [token, startLoading, stopLoading]);


  useEffect(() => {
    const applyFilters = () => {
      let filtered = data;

      // dataset filter first
      if (filters.selectedDatasets && filters.selectedDatasets.length > 0) {
        const selectedSet = new Set(filters.selectedDatasets);
        const datasets = filterOptions.interviewDatasets || [];

        const selectedIndustryIds = new Set(
          datasets
            .filter((d) => d.kind === 'industry' && selectedSet.has(d.key))
            .map((d) => d.industry_id)
        );
        const selectedProductIds = new Set(
          datasets
            .filter((d) => d.kind === 'product' && selectedSet.has(d.key))
            .map((d) => d.product_id)
        );
        const includeSelfUpload = selectedSet.has('self_upload');

        filtered = filtered.filter((conv) => {
          // default missing origin to "self_upload"
          const origin = conv.origin || 'self_upload';

          if (origin === 'self_upload') {
            return includeSelfUpload;
          }

          if (origin === 'ai_interview') {
            const inds = new Set(conv.industry_ids || []);
            const prods = new Set(conv.product_ids || []);
            const matchesIndustry =
              [...inds].some((id) => selectedIndustryIds.has(id));
            const matchesProduct =
              [...prods].some((id) => selectedProductIds.has(id));
            return matchesIndustry || matchesProduct;
          }

          // Unknown origin -> keep it out when a dataset filter is active
          return false;
        });
      }

      // other filters
      if (filters.selectedSource.length > 0) {
        filtered = filtered.filter(conv => filters.selectedSource.includes(conv.conversation_source));
      }
      if (filters.selectedPersona.length > 0) {
        filtered = filtered.filter(conv => filters.selectedPersona.includes(conv.segmentation_data?.persona));
      }
      if (filters.selectedJobToBeDone.length > 0) {
        filtered = filtered.filter(conv => conv.jobs_to_be_done?.some(job => filters.selectedJobToBeDone.includes(job.job_to_be_done_category)));
      }
      if (filters.selectedDesiredOutcome.length > 0) {
        filtered = filtered.filter(conv => conv.jobs_to_be_done?.some(job => filters.selectedDesiredOutcome.includes(job.desired_outcome_category)));
      }
      if (filters.selectedSolutionChoice.length > 0) {
        filtered = filtered.filter(conv => filters.selectedSolutionChoice.includes(conv.solution_choice_category));
      }
      if (filters.selectedCustomTags.length > 0) {
        filtered = filtered.filter(conv => conv.segmentation_data?.custom_tags?.some(tag => filters.selectedCustomTags.includes(tag)));
      }

      const currentDate = new Date();
      if (filters.dateRange === '6 months') {
        const sixMonthsAgo = new Date(currentDate.setMonth(currentDate.getMonth() - 6));
        filtered = filtered.filter(conv => new Date(conv.conversation_date) >= sixMonthsAgo);
      } else if (filters.dateRange === '3 months') {
        const threeMonthsAgo = new Date(currentDate.setMonth(currentDate.getMonth() - 3));
        filtered = filtered.filter(conv => new Date(conv.conversation_date) >= threeMonthsAgo);
      } else if (filters.dateRange === 'Custom' && filters.customDateRange.from && filters.customDateRange.to) {
        const fromDate = new Date(filters.customDateRange.from);
        const toDate = new Date(filters.customDateRange.to);
        filtered = filtered.filter(conv => new Date(conv.conversation_date) >= fromDate && new Date(conv.conversation_date) <= toDate);
      }

      setFilteredData(filtered);
    };

    applyFilters();
  }, [filters, data]);

  const updateFilters = (newFilters) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  const clearFilters = () => {
    setFilters({
      selectedSource: [],
      selectedPersona: [],
      selectedJobToBeDone: [],
      selectedDesiredOutcome: [],
      selectedSolutionChoice: [],
      selectedCustomTags: [],
      dateRange: 'All time',
      customDateRange: { from: null, to: null },
    });
  };

  return (
    <DataAndFilterContext.Provider value={{ filterOptions, filters, filteredData, updateFilters, clearFilters, fetchUniqueTags, updateDataFile }}>
      {children}
    </DataAndFilterContext.Provider>
  );
};
